import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  tableCellClasses,
  Typography,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useDispatch } from 'react-redux';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// components
import Page from '../../components/Page';
import BrudCrumbs from '../../components/BreadCrumbs';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import UserMoreMenu from '../../components/_dashboard/user/UserMoreMenu';
import partnerService from '../services/partnerService';
import { fDate } from '../../utils/formatTime';
import PartnersListToolbar from './PartnersListToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getOnboardingsList } from '../../store/Slice/Onboarding/OnboardingSlice';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useDebounce from '../../utils/useDebounce';
import {
  getCityData,
  getDocsTypesData,
  getPartnerCompanyType,
  getPartnerTypesData,
  getStateData
} from '../../store/Slice/Global/GlobalSlice';

// ----------------------------------------------------------------------
const { UpdatePartnerStatus, PartnerStatusTypes } = partnerService;

const TABLE_HEAD = [
  { id: 'id', label: 'PARTNER ID', sortable: true },
  { id: 'partnerName', label: 'PARTNER NAME', sortable: true },
  { id: 'partnerType', label: 'PARTNER TYPE', sortable: true },
  { id: 'city', label: 'LOCATION', sortable: true },
  { id: 'randomPhoneNumber', label: 'MOBILE', sortable: true },
  { id: 'created', label: 'DOB / DOI', sortable: true },
  { id: 'active', label: 'STATUS', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

const getStatusBadgeStyles = (statusName) => {
  const s = String(statusName || '').toLowerCase();
  if (s === 'rejected') {
    return { bgcolor: '#fee2e2', color: '#dc2626' };
  }
  if (s === 'activated') {
    return { bgcolor: '#dcfce7', color: '#16a34a' };
  }
  if (s === 'inactive') {
    return { bgcolor: '#f1f5f9', color: '#64748b' };
  }
  return { bgcolor: '#fef3c7', color: '#d97706' };
};

export default function PartnersList() {
  const pageCode = 'PARTNER_PAGE';
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('partnerName');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageMenu, setPageMenu] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [filterState, setFilterState] = useState({
    typeId: null,
    partnerStatusTypeId: null,
    city: null,
    cityId: null
  });
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { OnboardingListData } = useOnboardingSelector();
  const { AccessLevelConfig } = useGlobalSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  useEffect(() => {
    dispatch(getPartnerTypesData());
    dispatch(getDocsTypesData());
    dispatch(getPartnerCompanyType());
    dispatch(getStateData({ countryId: 101 }));
    dispatch(getCityData({ stateId: null }));
  }, [dispatch]);

  const getOnboardingListAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : '',
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order || 'desc'
      },
      filterModel: {
        typeId: filterState?.typeId || null,
        partnerStatusTypeId: filterState?.partnerStatusTypeId || null,
        cityId: filterState?.cityId || null
      }
    };
    dispatch(getOnboardingsList(data));
  }, [LoginData, debouncedSearch, page, rowsPerPage, order, filterState, dispatch]);

  useEffect(() => {
    getOnboardingListAPi();
  }, [getOnboardingListAPi]);

  const handleRequestSort = (property) => {
    if (property !== 'action') {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = OnboardingListData?.map((n) => n) || [];
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, partner) => {
    const selectedIndex = selected.indexOf(partner);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, partner);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  useEffect(() => {
    if (Array.isArray(OnboardingListData)) {
      setStatusOptions((prev) => {
        const map = new Map();
        prev.forEach((opt) => {
          map.set(opt.value, opt);
        });
        OnboardingListData.forEach((partner) => {
          const status = partner?.partnerStatus;
          const value = status?.typeId;
          const label = status?.typeName;
          if (value && label && !map.has(value)) {
            map.set(value, { label, value });
          }
        });
        return Array.from(map.values());
      });
    }
  }, [OnboardingListData]);

  const UpdatePartnerStatusApi = async (rowId, partner) => {
    const PartnerStatusTypesApi = await PartnerStatusTypes(LoginData?.jwt);
    if (PartnerStatusTypesApi.status === 'success') {
      let partnerStatusTypeId = '';
      PartnerStatusTypesApi.data.partnerStatusTypes.forEach((item) => {
        if (item.typeName === 'Activated') {
          partnerStatusTypeId = item.typeId;
        }
      });
      const statusCredential = {
        companyId: partner.companyId,
        partnerId: rowId,
        partnerStatusTypeId: partnerStatusTypeId
      };
      const UpdatePartnerStatusResponse = await UpdatePartnerStatus(
        statusCredential,
        LoginData?.jwt
      );
      if (UpdatePartnerStatusResponse.status === 'success') {
        getOnboardingListAPi();
      }
    }
  };

  useEffect(() => {
    if (AccessLevelConfig) {
      AccessLevelConfig?.accessPagesMapping?.forEach((accessPageMap) => {
        if (accessPageMap?.pageCode === pageCode) {
          setPageMenu(accessPageMap?.accessSubPagesMappings);
        }
      });
    }
  }, [AccessLevelConfig]);

  useEffect(() => {
    if (!OnboardingListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(OnboardingListData.length >= rowsPerPage);
  }, [OnboardingListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [filterName, debouncedSearch, filterState]);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const totalCount = Array.isArray(OnboardingListData) ? OnboardingListData.length : 0;
  const startRecord = totalCount > 0 ? page * rowsPerPage + 1 : 0;
  const endRecord = totalCount > 0 ? page * rowsPerPage + totalCount : 0;

  return (
    <Page title="Partner Management System || Partners">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <HandshakeOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Partners
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                View and manage registered partners, verification statuses, and partner details.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Main Content Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <PartnersListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            parent="partners"
            pageMenu={pageMenu}
            filterState={filterState}
            setFilterState={setFilterState}
            statusOptions={statusOptions}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 900, minHeight: 380 }}>
              <Table>
                <thead>
                  <TableRow>
                    <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                      <Checkbox
                        indeterminate={
                          selected.length > 0 && selected.length < (OnboardingListData?.length || 0)
                        }
                        checked={
                          OnboardingListData?.length > 0 &&
                          selected.length === OnboardingListData?.length
                        }
                        onChange={handleSelectAllClick}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                        }}
                      />
                    </StyledHeadCell>
                    {TABLE_HEAD.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                        sx={{
                          cursor: headCell.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                          spacing={0.5}
                        >
                          <span>{headCell.label}</span>
                          {headCell.sortable && (
                            <SwapVertIcon
                              sx={{
                                fontSize: 16,
                                color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                              }}
                            />
                          )}
                        </Stack>
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {Array.isArray(OnboardingListData) &&
                    OnboardingListData?.map((partner, index) => {
                      const isItemSelected = selected.indexOf(partner) !== -1;
                      const statusName = partner?.partnerStatus?.typeName;
                      const badgeStyles = getStatusBadgeStyles(statusName);

                      return (
                        <TableRow
                          hover
                          key={index}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:hover': { backgroundColor: '#f8fafc !important' },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: 'rgba(2, 132, 199, 0.06)'
                            }
                          }}
                        >
                          <StyledTableCell padding="checkbox" sx={{ pl: 2.5 }}>
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, partner)}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#0284c7' }
                              }}
                            />
                          </StyledTableCell>

                          <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                            {partner?.partnerState === null ||
                            partner?.partnerStatus?.typeName === 'Rejected' ||
                            partner?.partnerStatus?.typeName === 'Approved' ||
                            partner?.partnerStatus?.typeName === 'Inactive' ||
                            partner?.partnerStatus?.typeName === 'Incomplete'
                              ? ''
                              : partner?.partnerIdToDisplay}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {partner?.partnerName || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {partner?.partnerType?.typeName || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {partner?.cityEntity !== null ? partner?.cityEntity?.cityName : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {partner?.mobile || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {partner?.dob ? fDate(partner?.dob) : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '20px',
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                bgcolor: badgeStyles.bgcolor,
                                color: badgeStyles.color
                              }}
                            >
                              {statusName || 'No status'}
                            </Box>
                          </StyledTableCell>

                          <StyledTableCell align="center" width={80}>
                            <UserMoreMenu
                              partner={partner}
                              rowId={partner?.partnerId}
                              parent="partner"
                              pageMenu={pageMenu}
                              UpdatePartnerStatusApi={UpdatePartnerStatusApi}
                            />
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
                {(!OnboardingListData || OnboardingListData?.length === 0) && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell
                        align="center"
                        colSpan={TABLE_HEAD.length + 1}
                        sx={{ py: 6 }}
                      >
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderTop: '1px solid #f1f5f9',
              gap: 2
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Showing{' '}
              <span style={{ fontWeight: 600, color: '#334155' }}>
                {startRecord} to {endRecord}
              </span>{' '}
              records
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    height: 32,
                    fontSize: 13,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                    px: 1
                  }}
                >
                  {page + 1}
                </Box>

                <IconButton
                  size="small"
                  disabled={totalCount < rowsPerPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
